import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Lock, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { Reveal } from "../components/Reveal";
import Breadcrumbs from "../components/Breadcrumbs";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { api, setAdminToken } from "../lib/api";

function LockGate() {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password: pw });
      setAdminToken(data.token);
      toast.success("Welcome back, editor.");
      navigate("/blog-admin");
    } catch {
      toast.error("Incorrect password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {createPortal(
        <button
          onClick={() => setOpen(true)}
          aria-label="Editor access"
          title="Editor access"
          data-testid="blog-lock-icon"
          className="fixed bottom-6 right-6 z-[70] flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 bg-white shadow-lift opacity-70 transition-all hover:opacity-100 hover:scale-105"
        >
          <Lock className="h-4 w-4 text-primary" />
        </button>,
        document.body
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="blog-lock-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Editor Access</DialogTitle>
            <DialogDescription>Enter the editor password to manage the Numoraa journal.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="mt-2 space-y-4">
            <input
              type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)}
              placeholder="Enter password"
              className="w-full rounded-xl border border-border bg-background px-4 py-3 outline-none focus:border-primary"
              data-testid="blog-lock-password"
            />
            <button disabled={loading} data-testid="blog-lock-submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-primary-foreground disabled:opacity-60">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Unlock
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("All");
  const [search, setSearch] = useState("");

  const load = async (cat = active, q = search) => {
    setPosts(null);
    const params = {};
    if (cat && cat !== "All") params.category = cat;
    if (q) params.search = q;
    const { data } = await api.get("/blog/posts", { params });
    setPosts(data);
  };

  useEffect(() => {
    api.get("/blog/categories").then((r) => setCategories(["All", ...r.data]));
    load("All", "");
    // eslint-disable-next-line
  }, []);

  return (
    <div className="pt-28 md:pt-32">
      <SEO title="Numerology Blog" description="Learn numerology with Numoraa's journal — clear, beautiful guides on numbers, Lo Shu grids, palmistry, compatibility and remedies." path="/blog" />
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }]} />
        <Reveal>
          <h1 className="mt-6 font-serif text-5xl sm:text-6xl md:text-7xl font-light tracking-tighter">The <span className="italic text-primary">Journal</span></h1>
          <p className="mt-6 max-w-xl text-lg font-light text-muted-foreground">Stories, guides and gentle wisdom to help you read the language of numbers.</p>
        </Reveal>

        <div className="mt-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-2" data-testid="blog-categories">
            {categories.map((c) => (
              <button key={c} onClick={() => { setActive(c); load(c, search); }}
                className={`rounded-full px-4 py-2 text-sm transition-colors ${active === c ? "bg-primary text-primary-foreground" : "border border-border bg-white hover:border-primary"}`}>
                {c}
              </button>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); load(active, search); }} className="flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 md:w-72">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search articles…" className="flex-1 bg-transparent text-sm outline-none" data-testid="blog-search" />
          </form>
        </div>

        <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3 pb-8">
          {posts === null ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i}><Skeleton className="h-52 w-full rounded-3xl" /><Skeleton className="mt-4 h-5 w-3/4" /><Skeleton className="mt-2 h-4 w-full" /></div>
            ))
          ) : posts.length === 0 ? (
            <p className="text-muted-foreground col-span-full">No articles found. Try another search.</p>
          ) : (
            posts.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.08}>
                <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-soft transition-all duration-500 hover:shadow-lift hover:-translate-y-1">
                  <div className="relative h-52 overflow-hidden bg-secondary">
                    {p.cover_image ? (
                      <img src={p.cover_image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-secondary to-muted"><span className="font-serif text-5xl text-primary/20">✦</span></div>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="text-xs uppercase tracking-[0.15em] text-gold">{p.category} · {p.reading_time} min read</p>
                    <h2 className="mt-2 font-serif text-2xl leading-snug">{p.title}</h2>
                    <p className="mt-2 flex-1 text-sm text-muted-foreground leading-relaxed line-clamp-3">{p.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary">Read article <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                  </div>
                </Link>
              </Reveal>
            ))
          )}
        </div>

        <LockGate />
      </div>
    </div>
  );
}
